// Teacher-side notifications dropdown. Polls /notifications every 30 s and on
// open. Click an item to route to the relevant surface; the kind decides:
//
//   doubt_raised / doubt_message → /doubts/:doubt_id
//   attempt_submitted            → /tests/:displayId/submissions/:attemptId
//                                  (the student's submission detail, ready
//                                  to score — not the bulk list)
//   question_reported            → /tests/:displayId/submissions/:attemptId
//                                  (the student's submission, so the
//                                  flagged question is right there)
//   parent_chat_message          → /parent-doubts/:chat_id
//   parent_complaint_raised      → /parent-complaints (the queue; every entry
//                                  renders in full there)
//
// The bell sits in the MobileShell header next to the profile icon. Badge
// renders only when there is something to surface.

import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, MessageCircle, ClipboardList, AlertTriangle, MessageSquareReply, RotateCcw, MapPinOff, Hand, Moon, Clock, LogOut, KeyRound, UserPlus, LogIn, UserX, CalendarPlus, CalendarCheck, CalendarX, Megaphone, BellRing, MessagesSquare, ShieldAlert, type LucideIcon } from "lucide-react";
import {
  listNotifications, markNotificationRead, markAllNotificationsRead,
  type TeacherNotification,
} from "../services/teacherApi";

export const relTime = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Per-kind copy + icon + accent. Keeps the click handler trivial — every
// renderable thing comes from this map.
export const KIND_META: Record<TeacherNotification["kind"], {
  icon: LucideIcon;
  label: string;
  accent: string;
}> = {
  doubt_raised:      { icon: MessageCircle,        label: "New doubt",       accent: "bg-primary-500/20 text-primary-300" },
  doubt_message:     { icon: MessageSquareReply,   label: "Doubt reply",     accent: "bg-primary-500/20 text-primary-300" },
  attempt_submitted: { icon: ClipboardList,        label: "Submitted",       accent: "bg-emerald-500/20 text-emerald-300" },
  question_reported: { icon: AlertTriangle,        label: "Flagged Q",       accent: "bg-danger-500/20 text-danger-300" },
  retest_requested:  { icon: RotateCcw,            label: "Retest request",  accent: "bg-warning-500/20 text-warning-300" },
  attendance_checkout_override:
                     { icon: MapPinOff,            label: "Off-site out",    accent: "bg-amber-500/20 text-amber-300" },
  progress_extra_logged:
                     { icon: Moon,                 label: "Extra class",     accent: "bg-primary-500/20 text-primary-300" },
  attendance_checkin_reminder:
                     { icon: Clock,                label: "Check-in due",    accent: "bg-amber-500/20 text-amber-300" },
  attendance_checkout_reminder:
                     { icon: LogOut,               label: "Check-out",       accent: "bg-amber-500/20 text-amber-300" },
  attendance_checked_in:
                     { icon: LogIn,                label: "Checked in",      accent: "bg-emerald-500/20 text-emerald-300" },
  attendance_checked_out:
                     { icon: LogOut,               label: "Checked out",     accent: "bg-slate-500/20 text-slate-300" },
  attendance_absent_report:
                     { icon: UserX,                label: "Absent today",    accent: "bg-danger-500/20 text-danger-300" },
  leave_application_raised:
                     { icon: CalendarPlus,         label: "Leave request",   accent: "bg-warning-500/20 text-warning-300" },
  leave_application_approved:
                     { icon: CalendarCheck,        label: "Leave approved",  accent: "bg-emerald-500/20 text-emerald-300" },
  leave_application_rejected:
                     { icon: CalendarX,            label: "Leave declined",  accent: "bg-danger-500/20 text-danger-300" },
  staff_notice:      { icon: Megaphone,            label: "Notice",          accent: "bg-primary-500/20 text-primary-300" },
  staff_notice_reminder:
                     { icon: BellRing,             label: "Reminder",        accent: "bg-warning-500/20 text-warning-300" },
  password_reset_requested:
                     { icon: KeyRound,             label: "Password reset",  accent: "bg-primary-500/20 text-primary-300" },
  teacher_registration_pending:
                     { icon: UserPlus,             label: "New staff",       accent: "bg-primary-500/20 text-primary-300" },
  parent_chat_message:
                     { icon: MessagesSquare,       label: "Parent doubt",    accent: "bg-primary-500/20 text-primary-300" },
  parent_complaint_raised:
                     { icon: ShieldAlert,          label: "Parent complaint",accent: "bg-danger-500/20 text-danger-300" },
};

interface Props {
  onNavigate: (target: { pathname: string; hash?: string; state?: any }) => void;
  from?: string;
}

export default function NotificationsBell({ onNavigate, from }: Props) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<TeacherNotification[]>([]);
  const [unread, setUnread] = useState(0);
  // Refs for click-away detection. The original DOM-element catcher
  // (`<div fixed inset-0 z-40>`) didn't work because the parent header has
  // `backdrop-blur-md` — per spec backdrop-filter creates a containing block
  // for fixed-positioned descendants, so the catcher was clipped to the
  // header bounds and never received clicks from the page body. Listening on
  // document.mousedown sidesteps that entirely.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listNotifications();
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      // 401s during logout race or transient errors — silently skip.
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 30_000);
    return () => window.clearInterval(t);
  }, [refresh]);

  // Re-refresh when the dropdown is opened — items might have arrived since
  // the last 30 s poll.
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // Document-level click-away. Closes on any mousedown/touchstart outside
  // the bell+panel bounding box. Esc also closes for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (wrapRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const markOne = async (id: string) => {
    try { await markNotificationRead(id); } catch {}
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, read_at: new Date().toISOString() } : x)));
    setUnread((n) => Math.max(0, n - 1));
  };

  const markAll = async () => {
    try { await markAllNotificationsRead(); } catch {}
    setItems((xs) => xs.map((x) => (x.read_at ? x : { ...x, read_at: new Date().toISOString() })));
    setUnread(0);
  };

  // Route the click. Each kind has a "this is where the user wants to land"
  // mapping; if a payload is missing the field we expect, fall back gracefully.
  const handleClick = (n: TeacherNotification) => {
    if (!n.read_at) markOne(n.id);
    setOpen(false);
    const p = n.payload || {};
    // Tag every notification-driven navigation with from: "/notifications" so
    // the destination page's back arrow (useReferrerBack) returns to the
    // notifications panel — wherever the bell was opened from.
    const go = (pathname: string, hash?: string) =>
      onNavigate({ pathname, hash, state: { from: "/notifications" } });
    switch (n.kind) {
      case "doubt_raised":
      case "doubt_message":
        if (p.doubt_id) go(`/doubts/${encodeURIComponent(p.doubt_id)}`);
        else go("/doubts");
        return;
      case "attempt_submitted":
        // Deep-link straight to that student's submission so the teacher
        // lands on the grading surface, not the roster page they'd have
        // to click into again. Fall back to the roster only when the
        // payload didn't carry the attempt id (older rows).
        if (p.test_display_id && p.attempt_id) {
          go(`/tests/${encodeURIComponent(p.test_display_id)}/submissions/${encodeURIComponent(p.attempt_id)}`);
        } else if (p.test_display_id) {
          go(`/tests/${encodeURIComponent(p.test_display_id)}/submissions`);
        }
        return;
      case "question_reported":
        // Same logic: jump into the specific attempt so the flagged Q is
        // immediately visible. Reported tab is the fallback.
        if (p.test_display_id && p.attempt_id) {
          go(`/tests/${encodeURIComponent(p.test_display_id)}/submissions/${encodeURIComponent(p.attempt_id)}`);
        } else {
          go("/doubts", "#reported");
        }
        return;
      case "retest_requested":
        // Two flavours of retest request need different deep-links:
        //   • do-over   (has attempt_id) → AttemptPage shows the Approve /
        //                                  Deny banner inline with the
        //                                  student's existing answers.
        //   • no-show   (attempt_id null + p.no_show) → SubmissionsPage's
        //                                  "Pending retest requests" panel
        //                                  is where the teacher decides
        //                                  (there's no attempt to inspect).
        // Before this fix, no-show clicks fell through to a silent return
        // because the conditional required attempt_id.
        if (p.test_display_id && p.attempt_id) {
          go(`/tests/${encodeURIComponent(p.test_display_id)}/submissions/${encodeURIComponent(p.attempt_id)}`);
        } else if (p.test_display_id) {
          go(`/tests/${encodeURIComponent(p.test_display_id)}/submissions`);
        }
        return;
      case "attendance_checkout_override":
        // Off-site check-out warning (admin/HoD only). Land on the attendance
        // screen where the recent-check-ins card flags the override row.
        go("/attendance");
        return;
      case "attendance_checkin_reminder":
      case "attendance_checkout_reminder":
        go("/attendance");
        return;
      case "attendance_checked_in":
      case "attendance_checked_out":
      case "attendance_absent_report":
        // Admin/HoD oversight — the Recent check-ins table is where the punch
        // (or the hole where one should be) is actually inspectable.
        go("/attendance/checkins");
        return;
      case "leave_application_raised":
        // The approve/reject queue is inline on the Attendance hub, not a
        // sub-route of its own.
        go("/attendance");
        return;
      case "leave_application_approved":
      case "leave_application_rejected":
        // The applicant's own list, where the decision note is shown in full.
        go("/attendance/leaves");
        return;
      case "staff_notice":
      case "staff_notice_reminder":
        // ?open=<id> highlights the notice the banner referred to, since the
        // list can hold several. The query rides on `pathname` because
        // MobileShell's onNavigate only forwards pathname + hash — a `search`
        // field would be silently dropped.
        go(p.notice_id ? `/notices?open=${encodeURIComponent(p.notice_id)}` : "/notices");
        return;
      case "parent_chat_message":
        // A parent wrote in their thread with this teacher. Straight to it.
        if (p.chat_id) go(`/parent-doubts/${encodeURIComponent(p.chat_id)}`);
        else go("/parent-doubts");
        return;
      case "parent_complaint_raised":
        // The queue rather than a per-complaint route: every entry renders in
        // full there, and the list opens on Pending.
        go("/parent-complaints");
        return;
      case "password_reset_requested":
      case "teacher_registration_pending":
        // Handled from the admin panel, not here: the password-request queue
        // and the Teachers screen live there.
        // Nothing actionable on mobile, so just go home — the row itself still
        // carries the detail in the feed.
        go("/");
        return;
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        title="Notifications"
        className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors relative"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-[3px] rounded-full bg-primary-500 text-oncolor text-[9px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-away catcher lives at the document level (see effect above)
              rather than as a DOM element, because the header's backdrop-blur
              creates a containing block that would clip a `fixed inset-0`
              catcher to the header bounds. */}
          {/* Panel max-height pinned to viewport with safe-area padding so it
              never extends below the bottom nav on tall phones. Calc subtracts
              the header (~5rem) + bottom nav (~5rem) + a small breathing margin. */}
          <div ref={panelRef}
            className="absolute right-0 top-full mt-2 w-[min(22rem,calc(100vw-1.5rem))] max-h-[calc(100dvh-12rem)] overflow-y-auto z-50 rounded-xl border border-white/10 bg-slate-900/95 backdrop-blur-md shadow-2xl">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/5">
              <span className="text-sm font-medium text-white">Notifications</span>
              {unread > 0 && (
                <button onClick={markAll} className="text-[11px] text-primary-400 hover:text-primary-300">
                  Mark all read
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <div className="py-10 text-center text-slate-500 text-xs">No notifications yet.</div>
            ) : (
              items.map((n) => {
                const p = n.payload || {};
                // Physical hand-in (student with no phone/scanner) reads as a
                // distinct "collect & upload the sheet" task, not a plain
                // "submitted" — different label/icon/accent so the teacher knows
                // there's an action waiting. Click still lands on the same
                // submission page, where the bulk uploader sits.
                const isPhysicalHandin = n.kind === "attempt_submitted" && !!p.physical_handin;
                const meta = isPhysicalHandin
                  ? { icon: Hand, label: "Collect sheet", accent: "bg-amber-500/20 text-amber-300" }
                  : (KIND_META[n.kind] || KIND_META.doubt_raised);
                const Icon = meta.icon;
                const title = n.kind === "attempt_submitted"
                                ? (isPhysicalHandin
                                    ? <><b>{p.student_name || "A student"}</b> handed in paper · {p.test_title || p.test_display_id || "test"} — scan &amp; upload the sheet</>
                                    : <>Q{p.q_no ? "" : ""} <b>{p.student_name || "A student"}</b> · {p.test_title || p.test_display_id || "test"}</>)
                              : n.kind === "question_reported"
                                ? <><b>{p.student_name || "A student"}</b> flagged Q{p.q_no} · {p.test_title || ""}</>
                              : n.kind === "doubt_raised"
                                ? <><b>{p.student_name || "A student"}</b> raised a doubt · Q{p.q_no} · {p.test_title || ""}</>
                              : n.kind === "attendance_checkout_override"
                                ? <><b>{p.teacher_name || "A teacher"}</b> checked out off-site{p.distance != null ? <> · {Math.round(p.distance)}m from gate</> : null}</>
                              : n.kind === "progress_extra_logged"
                                ? <><b>{p.teacher_name || "A teacher"}</b> logged an outside-schedule class{Array.isArray(p.classes) && p.classes.length ? <> · {p.classes.join(", ")}</> : null}{p.subject_name ? <> · {p.subject_name}</> : null}</>
                              : n.kind === "attendance_checked_in"
                                ? <><b>{p.teacher_name || "A teacher"}</b> checked in{p.at_local ? <> · {p.at_local}</> : null}{p.mode === "fallback" ? <> · selfie fallback</> : null}</>
                              : n.kind === "attendance_checked_out"
                                ? <><b>{p.teacher_name || "A teacher"}</b> checked out{p.at_local ? <> · {p.at_local}</> : null}{p.hours_present != null ? <> · {Number(p.hours_present).toFixed(1)}h</> : null}</>
                              : n.kind === "attendance_absent_report"
                                ? <><b>{p.count ?? 0} staff absent</b> today{Array.isArray(p.names) && p.names.length ? <> · {p.names.join(", ")}</> : null}</>
                              : n.kind === "leave_application_raised"
                                ? <><b>{p.teacher_name || "A teacher"}</b> applied for leave{p.start_date ? <> · {p.start_date === p.end_date ? p.start_date : `${p.start_date} → ${p.end_date}`}</> : null}{p.reason_category ? <> · {p.reason_category}</> : null}</>
                              : n.kind === "leave_application_approved"
                                ? <>Your leave was <b>approved</b>{p.start_date ? <> · {p.start_date === p.end_date ? p.start_date : `${p.start_date} → ${p.end_date}`}</> : null}</>
                              : n.kind === "staff_notice" || n.kind === "staff_notice_reminder"
                                ? <><b>{p.title || "Staff notice"}</b>{p.when_label ? <> · {p.when_label}</> : null}{p.author_name ? <> · {p.author_name}</> : null}</>
                              : n.kind === "leave_application_rejected"
                                ? <>Your leave was <b>declined</b>{p.start_date ? <> · {p.start_date === p.end_date ? p.start_date : `${p.start_date} → ${p.end_date}`}</> : null}{p.decision_note ? <> — {p.decision_note}</> : null}</>
                              : n.kind === "parent_chat_message"
                                ? <><b>{p.parent_name || "A parent"}</b> asked about {p.student_name || "their child"}{p.subject_name ? <> · {p.subject_name}</> : null}{p.body_preview ? <> — {p.body_preview}</> : (p.has_attachments ? <> — sent a photo</> : null)}</>
                              : n.kind === "parent_complaint_raised"
                                ? <><b>{p.parent_name || "A parent"}</b> raised a complaint{p.student_name ? <> · about {p.student_name}</> : null}{p.about_preview ? <> — {p.about_preview}</> : null}</>
                              : /* doubt_message */
                                <><b>{p.student_name || "A student"}</b> replied · Q{p.q_no} · {p.test_title || ""}</>;
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className={`px-4 py-3 border-b border-white/5 cursor-pointer transition-colors ${
                      n.read_at ? "hover:bg-white/[0.02]" : "bg-primary-500/5 hover:bg-primary-500/10"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {!n.read_at && <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />}
                      <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded uppercase tracking-widest flex items-center gap-1 ${meta.accent}`}>
                        <Icon size={10} /> {meta.label}
                      </span>
                      <span className="text-[10px] text-slate-500 ml-auto">{relTime(n.created_at)}</span>
                    </div>
                    <div className="text-[12px] text-slate-200 line-clamp-2">{title}</div>
                    {p.body_preview && (
                      <div className="text-[11px] text-slate-400 line-clamp-2 mt-0.5">{p.body_preview}</div>
                    )}
                  </div>
                );
              })
            )}

            <button
              onClick={() => { setOpen(false); onNavigate({ pathname: "/notifications", state: { from } }); }}
              className="w-full text-center px-4 py-2.5 text-[11px] text-primary-400 hover:text-primary-300 border-t border-white/5"
            >
              View all notifications
            </button>
          </div>
        </>
      )}
    </div>
  );
}